import { createApp, ref, h } from "vue";
import { i18n } from "@/shared/config/i18n";
import AlertDialog from "./AlertDialog.vue";
import AlertDialogContent from "./AlertDialogContent.vue";
import AlertDialogTitle from "./AlertDialogTitle.vue";
import { AlertDialogDescription } from "reka-ui";
import Button from "@/shared/ui/button/Button.vue";

/**
 * Программный вызов confirm диалога в стиле shadcn
 *
 * @param {string} message - Текст сообщения
 * @param {string} [title] - Заголовок
 * @param {Object} [options] - Опции
 * @param {string} [options.confirmButtonText] - Текст кнопки подтверждения
 * @param {string} [options.cancelButtonText] - Текст кнопки отмены
 * @param {string} [options.type] - Тип: "warning" | "danger" | "info"
 * @returns {Promise<void>} Resolves при подтверждении, rejects при отмене
 *
 * @example
 * import { useConfirm } from "@/shared/ui/alert-dialog";
 * const confirm = useConfirm();
 * await confirm("Вы уверены?", "Внимание", { confirmButtonText: "Да", cancelButtonText: "Отмена" });
 */
export function useConfirm() {
  return function confirm(message, title, options = {}) {
    return new Promise((resolve, reject) => {
      const container = document.createElement("div");
      document.body.appendChild(container);

      const open = ref(true);

      const { confirmButtonText = "OK", cancelButtonText, type } = options;

      const confirmVariant = type === "danger" ? "destructive" : "default";

      function cleanup() {
        open.value = false;
        setTimeout(() => {
          app.unmount();
          container.remove();
        }, 200);
      }

      function onConfirm() {
        cleanup();
        resolve();
      }

      function onCancel() {
        cleanup();
        reject(new Error("cancel"));
      }

      const App = {
        setup() {
          return () =>
            h(AlertDialog, { open: open.value }, () =>
              h(
                AlertDialogContent,
                { class: "sm:max-w-[420px] gap-0 p-0" },
                () => [
                  h("div", { class: "p-6 flex flex-col gap-2 " }, [
                    title ? h(AlertDialogTitle, null, () => title) : null,
                    h(
                      AlertDialogDescription,
                      { class: "text-sm text-muted-foreground" },
                      () => message
                    ),
                  ]),
                  h(
                    "div",
                    {
                      class:
                        "flex justify-end gap-2 px-6 py-4 border-t border-border bg-muted/50",
                    },
                    [
                      cancelButtonText
                        ? h(
                            Button,
                            { variant: "outline", onClick: onCancel },
                            () => cancelButtonText
                          )
                        : null,
                      h(
                        Button,
                        { variant: confirmVariant, onClick: onConfirm },
                        () => confirmButtonText
                      ),
                    ]
                  ),
                ]
              )
            );
        },
      };

      const app = createApp(App);
      if (i18n) {
        app.use(i18n);
      }
      app.mount(container);
    });
  };
}
